import { Preferences } from '@capacitor/preferences';
import { Device } from '@capacitor/device';

class DeviceService {
  constructor() {
    this.deviceUuidKey = 'device_uuid';
    this.deviceUuid = null;
  }

  /**
   * UUIDを生成する
   * @returns {string} 生成されたUUID
   */
  generateUUID() {
    // RFC 4122 version 4 UUID を生成
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * 端末のUUIDを取得または生成
   * @returns {Promise<string>} 端末のUUID
   */
  async getDeviceUUID() {
    try {
      // 既にメモリにキャッシュされている場合はそれを返す
      if (this.deviceUuid) {
        return this.deviceUuid;
      }

      // Preferencesから既存のUUIDを取得
      const { value: existingUuid } = await Preferences.get({
        key: this.deviceUuidKey
      });

      if (existingUuid) {
        // 既存のUUIDが存在する場合
        this.deviceUuid = existingUuid;
        return existingUuid;
      } else {
        // UUIDが存在しない場合は新規生成
        const newUuid = this.generateUUID();
        
        // Preferencesに保存
        await Preferences.set({
          key: this.deviceUuidKey,
          value: newUuid
        });

        this.deviceUuid = newUuid;
        return newUuid;
      }
    } catch (error) {
      
      // エラーが発生した場合は一時的なUUIDを生成（保存はしない）
      const tempUuid = this.generateUUID();
      console.warn('一時的なUUIDを使用します:', tempUuid);
      return tempUuid;
    }
  }

  /**
   * 端末UUIDをリセット（新しいUUIDを生成）
   * @returns {Promise<string>} 新しいUUID
   */
  async resetDeviceUUID() {
    try {
      const newUuid = this.generateUUID();
      
      await Preferences.set({
        key: this.deviceUuidKey,
        value: newUuid
      });

      this.deviceUuid = newUuid;
      return newUuid;
    } catch (error) {
      console.error('端末UUIDリセットエラー:', error);
      throw error;
    }
  }

  /**
   * 端末UUIDを削除
   * @returns {Promise<void>}
   */
  async removeDeviceUUID() {
    try {
      await Preferences.remove({
        key: this.deviceUuidKey
      });
      
      this.deviceUuid = null;
    } catch (error) {
      console.error('端末UUID削除エラー:', error);
      throw error;
    }
  }

  /**
   * 端末情報を取得
   * @returns {Promise<Object>} 端末情報
   */
  async getDeviceInfo() {
    try {
      const uuid = await this.getDeviceUUID();

      const info = await Device.getInfo();
      
      return {
        uuid: uuid,
        platform: info.platform || 'Unknown',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('端末情報取得エラー:', error);
      throw error;
    }
  }
}

// シングルトンインスタンス
const deviceService = new DeviceService();

export default deviceService;